import {
  SubpageHero,
  SubpageLeistungen,
  SubpageProzess,
  SubpageBewertungen,
  SubpageReferenzen,
  SubpageKontakt,
  SubpageFAQ,
  SubpageSEO,
} from "@/components/shared/SubpageComponents";
import { SEOHead } from "@/components/shared/SEOHead";
import {
  Building2,
  HardHat,
  Wrench,
  Warehouse,
  Shovel,
  Layers,
} from "lucide-react";

const services = [
  { icon: Wrench, title: "Bausanierung", description: "Fachgerechte Sanierung und Modernisierung bestehender Gebäude und Bauwerke." },
  { icon: Shovel, title: "Tiefbau", description: "Erd- und Kanalbauarbeiten sowie Infrastrukturprojekte mit modernster Technik." },
  { icon: Layers, title: "Kellerabdichtungen", description: "Professionelle Abdichtung von Kellern gegen Feuchtigkeit und drückendes Wasser." },
  { icon: Building2, title: "Kellersanierungen", description: "Umfassende Sanierung feuchter und beschädigter Kellerräume." },
  { icon: HardHat, title: "Pflasterarbeiten", description: "Fachgerechte Pflaster- und Wegearbeiten für Einfahrten, Terrassen und Gehwege." },
  { icon: Shovel, title: "Grundstücksentwässerung", description: "Planung und Ausführung von Entwässerungssystemen für Grundstücke und Gebäude." },
];

const reviews = [
  { quote: "Die Firma Freitag hat unser gesamtes Bauunternehmen vom Anfang bis zum Ende begleitet. Wir waren und sind sehr zufrieden. Das Preis-Leistungsverhältnis ist gut, Termine werden exakt eingehalten, auftretende Reklamationen werden umgehend und ohne Diskussion erledigt. Besonders zu erwähnen ist der Vorarbeiter Heiko, der seine Männer gut anleitet.", author: "Matthias Lücke", company: "Google Bewertung" },
  { quote: "Einfach nur top. Firma Freitag hat sich um unsere Badezimmer-Renovierung gekümmert. Es hat alles hervorragend geklappt. Bin sehr zufrieden. Immer wieder gerne.", author: "Carmen Behrens", company: "Google Bewertung" },
  { quote: "Wir haben ein Haus erworben, bei dem die Terrasse und der Weg komplett neu gepflastert werden mussten. Das Unternehmen ist bekannt durch seine günstigen Preise und dennoch qualitativ guter und sauberer Arbeit – und das können wir auch wirklich genauso bestätigen. Wir können die Firma Freitag wirklich JEDEM empfehlen!", author: "Florian Fricke", company: "Google Bewertung" },
  { quote: "Lief alles super. Revisionsschacht im Vorgarten wurde preisgünstig realisiert. Immer wieder. Beste Grüße", author: "Fa Schneider", company: "Google Bewertung" },
];

const references = [
  { project: "Kellersanierung Altbau Innenstadt", scope: "Komplettabdichtung & Sanierung", year: "2024" },
  { project: "Pflasterarbeiten Wohnanlage", scope: "Einfahrten & Gehwege, 800m²", year: "2024" },
  { project: "Tiefbau Kanalarbeiten", scope: "Kanalsanierung & Neuverlegung", year: "2023" },
  { project: "Kellerabdichtung Mehrfamilienhaus", scope: "Druckwasserabdichtung", year: "2023" },
  { project: "Bausanierung Bestandsgebäude", scope: "Fassade & Mauerwerk", year: "2022" },
  { project: "Terrasse & Hofbefestigung", scope: "Pflasterarbeiten 350m²", year: "2022" },
];

const faqs = [
  { question: "Welche Bauleistungen bieten Sie an?", answer: "Wir bieten Bausanierung, Kellerabdichtungen, Kellersanierungen, Tiefbau und Pflasterarbeiten. Von der ersten Beratung bis zur Abnahme – alles aus einer Hand." },
  { question: "In welchen Regionen sind Sie tätig?", answer: "Wir sind im Raum Braunschweig und Umgebung (ca. 20 km Umkreis) tätig. Für Anfragen aus der Region stehen wir Ihnen gerne zur Verfügung." },
  { question: "Wie läuft die Zusammenarbeit ab?", answer: "Nach einem persönlichen Erstgespräch erstellen wir ein detailliertes Angebot. Nach Auftragserteilung folgen Planungsphase, Ausführung und gemeinsame Abnahme." },
  { question: "Bieten Sie auch Kellerabdichtungen an?", answer: "Ja, Kellerabdichtungen und Kellersanierungen gehören zu unseren Kernleistungen. Wir beraten Sie gerne zu den verschiedenen Abdichtungsmethoden." },
];

const seoText = [
  "Die Freitag Bauunternehmung ist Ihr erfahrener Partner für Bausanierung, Kellerabdichtungen, Tiefbau und Pflasterarbeiten im Raum Braunschweig. Seit über 25 Jahren realisieren wir anspruchsvolle Bauprojekte. Als Teil der Freitag-Gruppe profitieren unsere Kunden von einem starken Netzwerk aus Bau, Landschaftsbau und Sachverständigenwesen.",
  "Unsere Bauleistungen umfassen Bausanierung, Kellerabdichtungen, Kellersanierungen, Tiefbau und Pflasterarbeiten. Mit modernster Bautechnologie und handwerklicher Erfahrung garantieren wir höchste Qualität bei jedem Projekt. Termintreue und Budgetsicherheit sind für uns selbstverständlich.",
  "Ob feuchter Keller, sanierungsbedürftige Bausubstanz oder professionelle Pflasterarbeiten – die Freitag Bauunternehmung steht für Präzision, Zuverlässigkeit und handwerkliche Exzellenz im Raum Braunschweig. Kontaktieren Sie uns für eine unverbindliche Erstberatung.",
];

export default function BauunternehmungPage() {
  return (
    <div>
      <SEOHead
        title="Bauunternehmung Braunschweig | Bausanierung, Tiefbau & Pflasterarbeiten | Freitag-Gruppe"
        description="Bauunternehmung Freitag GmbH in Braunschweig: Bausanierung, Kellerabdichtungen, Kellersanierungen, Tiefbau und Pflasterarbeiten. Über 25 Jahre Erfahrung. 4.9 Sterne auf Google. Jetzt Anfrage stellen!"
        canonical="/bauunternehmung"
        schemaKey="bauunternehmung"
      />
      <SubpageHero
        color="blue"
        title="Bauunternehmung"
        subtitle="Freitag Bauunternehmung"
        description="Bausanierung, Kellerabdichtungen, Tiefbau und Pflasterarbeiten im Raum Braunschweig. Zuverlässig und termingerecht."
        image="/images/bau-hero.jpg"
      />
      <SubpageLeistungen color="blue" services={services} />
      <SubpageSEO
        color="blue"
        title="Freitag Bauunternehmung – Ihr Baupartner für anspruchsvolle Projekte"
        text={seoText}
      />
      <SubpageReferenzen color="blue" references={references} />
      <SubpageProzess color="blue" />
      <SubpageBewertungen color="blue" reviews={reviews} />
      <SubpageKontakt color="blue" title="Bauanfrage" />
      <SubpageFAQ color="blue" faqs={faqs} />
    </div>
  );
}
